//
//  TokenUtilPreviewMetadataGenerator.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { TextCase, TextDecoration } from "../../exports";
import { BorderStyle } from "../../model/enums/SDKBorderStyle";
import { TokenType } from "../../model/enums/SDKTokenType";
import { ColorToken } from "../../model/tokens/SDKColorToken";
import { DimensionToken, } from "../../model/tokens/SDKDimensionToken";
import { TokenTransform } from "../token-transform/SDKToolsTokenTransform";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Utility
export class TokenUtilPreviewStyleGenerator {
    resolvedStyleForToken(token, overridenStyles) {
        var _a, _b, _c, _d;
        switch (token.tokenType) {
            case TokenType.color: {
                const t = token;
                return {
                    background: ColorToken.colorValueToHex8(t.value).toUpperCase(),
                };
            }
            case TokenType.blur: {
                return undefined;
            }
            case TokenType.shadow: {
                const t = token;
                const transform = new TokenTransform();
                return {
                    boxShadow: transform.shadowsTokenToCSS(t.value),
                };
            }
            case TokenType.gradient: {
                const t = token;
                const transform = new TokenTransform();
                const layer = t.value[0];
                if (layer) {
                    return {
                        background: transform.gradientTokenToCSS(layer),
                    };
                }
                return undefined;
            }
            case TokenType.fontFamily: {
                const t = token;
                return {
                    fontFamily: (_a = overridenStyles === null || overridenStyles === void 0 ? void 0 : overridenStyles.fontFamily) !== null && _a !== void 0 ? _a : t.value.text,
                };
            }
            case TokenType.fontWeight: {
                const t = token;
                return {
                    fontWeight: (_b = overridenStyles === null || overridenStyles === void 0 ? void 0 : overridenStyles.fontWeight) !== null && _b !== void 0 ? _b : t.value.text,
                };
            }
            case TokenType.fontSize: {
                const t = token;
                return {
                    fontSize: `${DimensionToken.valueToString(t.value)})}`,
                };
            }
            case TokenType.lineHeight: {
                const t = token;
                return {
                    lineHeight: `${DimensionToken.valueToString(t.value)})}`,
                };
            }
            case TokenType.letterSpacing: {
                const t = token;
                return {
                    letterSpacing: `${DimensionToken.valueToString(t.value)})}`,
                };
            }
            case TokenType.paragraphSpacing: {
                const t = token;
                return {
                    letterSpacing: `${DimensionToken.valueToString(t.value)})}`,
                };
            }
            case TokenType.opacity: {
                const opacity = token;
                return {
                    opacity: `${opacity.value.measure}`,
                };
            }
            case TokenType.border: {
                const t = token;
                let style = "solid";
                const widthCssUnit = DimensionToken.unitToString(t.value.width.unit);
                switch (t.value.style) {
                    case BorderStyle.dashed:
                        style = "dashed";
                        break;
                    case BorderStyle.dotted:
                        style = "dotted";
                        break;
                    default:
                        break;
                }
                return {
                    borderWidth: `${t.value.width.measure}${widthCssUnit}`,
                    borderColor: ColorToken.colorValueToHex8(t.value.color).toUpperCase(),
                    borderStyle: style,
                };
            }
            case TokenType.radius: {
                const t = token;
                const cssUnit = DimensionToken.unitToString(t.value.unit);
                return {
                    borderRadius: `${t.value.measure}${cssUnit}`,
                };
            }
            case TokenType.borderWidth: {
                const t = token;
                const cssUnit = DimensionToken.unitToString(t.value.unit);
                return {
                    borderWidth: `${t.value.measure}${cssUnit}`,
                };
            }
            case TokenType.duration: {
                return undefined;
            }
            case TokenType.zIndex: {
                return undefined;
            }
            case TokenType.textCase: {
                return undefined;
            }
            case TokenType.textDecoration: {
                return undefined;
            }
            case TokenType.visibility: {
                return undefined;
            }
            case TokenType.productCopy: {
                return undefined;
            }
            case TokenType.string: {
                return undefined;
            }
            case TokenType.dimension: {
                const t = token;
                return {
                    width: DimensionToken.valueToString(t.value),
                };
            }
            case TokenType.size: {
                const t = token;
                return {
                    width: DimensionToken.valueToString(t.value),
                };
            }
            case TokenType.space: {
                const t = token;
                return {
                    width: DimensionToken.valueToString(t.value),
                };
            }
            case TokenType.typography: {
                const t = token;
                let textDecoration = "none";
                let textTransform = "none";
                switch (t.value.textDecoration.value) {
                    case TextDecoration.underline:
                        textDecoration = "underline";
                        break;
                    case TextDecoration.strikethrough:
                        textDecoration = "line-through";
                        break;
                    default:
                        break;
                }
                switch (t.value.textCase.value) {
                    case TextCase.upper:
                        textTransform = "uppercase";
                        break;
                    case TextCase.lower:
                        textTransform = "lowercase";
                        break;
                    case TextCase.camel:
                        textTransform = "capitalize";
                        break;
                    case TextCase.smallCaps:
                        // Not supported in preview just yet
                        break;
                    default:
                        break;
                }
                return {
                    fontSize: DimensionToken.valueToString(t.value.fontSize),
                    lineHeight: t.value.lineHeight
                        ? DimensionToken.valueToString(t.value.lineHeight)
                        : "normal",
                    fontWeight: (_c = overridenStyles === null || overridenStyles === void 0 ? void 0 : overridenStyles.fontWeight) !== null && _c !== void 0 ? _c : t.value.fontWeight.text,
                    fontFamily: (_d = overridenStyles === null || overridenStyles === void 0 ? void 0 : overridenStyles.fontFamily) !== null && _d !== void 0 ? _d : t.value.fontFamily.text,
                    letterSpacing: DimensionToken.valueToString(t.value.letterSpacing),
                    textTransform,
                    textDecoration,
                };
            }
            default:
                throw new Error(`Unknown token type`);
        }
    }
}
